// SCR-PURCHASES и MOD-REFUND-NOTICE (E12-T15, ux/08 §10–11): история — GET /shop/purchases, уведомления о
// возврате — bootstrap.refundNotices и подтверждение POST /me/refund-notices/{orderId}/ack (Р-86).
// Самостоятельного «Вернуть деньги» нет: возврат — через поддержку.
import { useEffect, useState } from 'preact/hooks';
import type { Api, ResponseOf } from '@garages/contract';
import { useApp, type App } from '../app/app.js';
import { t as tr } from '../i18n/t.js';
import { onWs } from '../net/ws.js';
import { PACK_NAMES } from '../pay/flow.js';
import { refundNotices, setWallet, wallet } from '../state/game.js';
import type { ScreenProps, SheetProps } from '../ui/App.js';
import { ErrorBanner, ScreenTitle, Sheet, useActionBar } from '../ui/kit.js';
import { showToast } from '../ui/toast.js';
import { useLoad } from '../ui/use-load.js';
import { I } from '../assets/icons.js';
import { Stars, StarsIcon } from './store-ui.js';
import { tn } from './store-model.js';

type Purchase = ResponseOf<Api['getPurchases']>['items'][number];
export type OrderStatusView = 'credited' | 'pending' | 'refunded' | 'cancelled';
type RefundNotice = NonNullable<ResponseOf<Api['getBootstrap']>['refundNotices']>[number];

const SUPPORT = 'https://t.me/garages_support_bot';

/** Статус заказа сервера → строка списка (§10). Счёт выдан, но не оплачен — в истории не показываем. */
export function statusView(s: Purchase['status']): OrderStatusView | null {
  if (s === 'credited') return 'credited';
  if (s === 'paid' || s === 'pre_checkout_ok') return 'pending';
  if (s === 'refunded') return 'refunded';
  if (s === 'cancelled' || s === 'expired' || s === 'failed') return 'cancelled';
  return null;
}

const STATUS_KEY: Record<OrderStatusView, string> = {
  credited: 'CL-PURCH-CREDITED', pending: 'CL-PURCH-PENDING', refunded: 'CL-PURCH-REFUNDED', cancelled: 'CL-PURCH-CANCELLED',
};

async function copy(text: string): Promise<void> {
  try { await navigator.clipboard.writeText(text); showToast(tr('CL-PURCH-COPIED'), 'success'); } catch { showToast(text, 'info'); }
}

const fmtDate = (iso: string) => new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export function PurchasesScreen(_p: ScreenProps) {
  const app = useApp();
  useActionBar({ back: true });
  const [more, setMore] = useState<Purchase[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const load = useLoad(() => app.call('getPurchases', { query: { limit: 30 } }), { onData: (d) => { setMore([]); setCursor(d.nextCursor); } });
  const next = async () => {
    if (!cursor) return;
    try { const d = await app.call('getPurchases', { query: { limit: 30, cursor } }); setMore((m) => [...m, ...d.items]); setCursor(d.nextCursor); } catch (e) { app.handleError(e); }
  };
  const rows = [...(load.data?.items ?? []), ...more].filter((r) => statusView(r.status));
  return (
    <>
      <ScreenTitle>{tr('CL-STORE-PURCHASES')}</ScreenTitle>
      <div class="content screen-bg store" data-testid="SCR-PURCHASES">
        <div class="page col store-page">
          <div class="store-panel purch-panel col">
          <div class="list">
            <div class="list-row" aria-disabled="true" data-testid="my-spending"><StarsIcon size={20} /><span style={{ flex: 1 }}>{tr('CL-PURCH-SPENDING')}</span><span class="small muted">{tr('CL-STORE-SOON')}</span></div>
            <div class="list-row" aria-disabled="true" data-testid="spend-limit-row"><I n="lock" size={20} /><span style={{ flex: 1 }}>{tr('CL-PURCH-LIMIT')}</span><span class="small muted">{tr('CL-STORE-SOON')}</span></div>
          </div>
          {load.error && !load.offline ? <ErrorBanner what={tr('CL-PURCH-WHAT')} onRetry={load.reload} /> : null}
          {load.offline ? <div class="banner" data-testid="purch-offline">{tr('CL-PURCH-OFFLINE')}</div> : null}
          {!load.data && load.loading ? <div class="col">{[0, 1, 2].map((i) => <div key={i} class="skeleton" style={{ height: 64 }} />)}</div> : null}
          {load.data && !rows.length ? <div class="empty card" data-testid="purch-empty"><I n="shop" size={48} class="ill" />{tr('CL-PURCH-EMPTY')}</div> : null}
          {rows.length ? (
            <div class="list" data-testid="purch-list">
              {rows.map((r) => {
                const st = statusView(r.status)!;
                return (
                  <button key={r.orderId} class="list-row purch-row" data-testid={`order-${r.orderId}`} onClick={() => app.nav.openSheet('MOD-PURCHASE-DETAILS', { row: r })}>
                    <span class="col" style={{ gap: 2, flex: 1 }}>
                      <b>{PACK_NAMES[r.productId] ?? tr('CL-PURCH-ORDER')}</b>
                      <span class="small muted">{fmtDate(r.createdAt)}</span>
                    </span>
                    <span class="col" style={{ gap: 2, alignItems: 'flex-end' }}>
                      <Stars n={r.priceXtr} />
                      <span class={`small purch-st st-${st}`}>{tr(STATUS_KEY[st])}</span>
                    </span>
                  </button>
                );
              })}
              {cursor ? <button class="btn small outline" data-testid="purch-more" onClick={() => { void next(); }}>{tr('CL-PURCH-MORE')}</button> : null}
            </div>
          ) : null}
          </div>
          <p class="small muted store-note">{tr('CL-PURCH-NO-SELF-REFUND')}</p>
        </div>
      </div>
    </>
  );
}

/** Лист заказа: номер и id платежа Telegram копируются тапом; «Проблема с покупкой» → поддержка с номером (§10). */
export function PurchaseDetailsSheet({ entry }: SheetProps) {
  const app = useApp();
  const r = entry.params['row'] as Purchase;
  const st = statusView(r.status) ?? 'cancelled';
  return (
    <Sheet entry={entry} title={PACK_NAMES[r.productId] ?? tr('CL-PURCH-ORDER')} actions={[
      { text: tr('CL-PURCH-PROBLEM'), testId: 'purch-problem', onClick: () => app.wa?.openTelegramLink?.(`${SUPPORT}?start=order_${r.orderId.replace(/-/g, '')}`) },
    ]}>
      <div class="col" data-testid="purch-details">
        <span class="row" style={{ justifyContent: 'space-between' }}><span class={`purch-st st-${st}`}>{tr(STATUS_KEY[st])}</span><Stars n={r.priceXtr} /></span>
        <span class="small muted">{fmtDate(r.createdAt)}{r.creditedAt ? ` · ${tr('CL-PURCH-CREDITED-AT', { t: fmtDate(r.creditedAt) })}` : ''}</span>
        <button class="list-row" data-testid="copy-order" onClick={() => { void copy(r.orderId); }}>
          <span class="col" style={{ gap: 2, flex: 1 }}><span class="small muted">{tr('CL-PURCH-ORDER-NO')}</span><code class="order-id">{r.orderId}</code></span>
          <span class="small accent-text">{tr('CL-PURCH-COPY')}</span>
        </button>
        {r.tgChargeId ? (
          <button class="list-row" data-testid="copy-charge" onClick={() => { void copy(r.tgChargeId!); }}>
            <span class="col" style={{ gap: 2, flex: 1 }}><span class="small muted">{tr('CL-PURCH-CHARGE')}</span><code class="order-id">{r.tgChargeId}</code></span>
            <span class="small accent-text">{tr('CL-PURCH-COPY')}</span>
          </button>
        ) : null}
      </div>
    </Sheet>
  );
}

/**
 * MOD-REFUND-NOTICE: «Возврат по заказу {id}: {n} ★ вернулись. Списано: {k} талонов»; при долге — строка о долге
 * (§11). «Понятно» подтверждает показ на сервере (POST /me/refund-notices/{orderId}/ack, Р-86).
 */
export function RefundNoticeSheet({ entry }: SheetProps) {
  const app = useApp();
  const n = entry.params as Partial<RefundNotice>;
  const orderId = String(n.orderId ?? '');
  const debt = n.talonsDebt ?? 0;
  useEffect(() => { app.haptic('warning'); }, []);
  const ok = () => {
    void app.call('ackRefundNotice', { params: { orderId }, body: {} }).catch(() => {});
    refundNotices.value = refundNotices.value.filter((x) => x.orderId !== orderId);
    app.nav.popSheet();
  };
  return (
    <Sheet entry={entry} title={tr('CL-REFUND-TITLE')} actions={[{ text: tr('CL-REFUND-OK'), testId: 'refund-ok', onClick: ok }]}>
      <div class="col" data-testid="MOD-REFUND-NOTICE-body">
        <p style={{ margin: 0 }}>{tr('CL-REFUND-TEXT', { id: orderId.slice(0, 8) })}{n.xtr ? <> <Stars n={n.xtr} /></> : null}</p>
        {n.debitedTalons ? <p style={{ margin: 0 }}>{tr('CL-REFUND-CLAWED', tn(n.debitedTalons))}</p> : null}
        {debt > 0 ? <div class="banner warn" data-testid="refund-debt">{tr('CL-REFUND-DEBT', tn(debt))}</div> : null}
      </div>
    </Sheet>
  );
}

/**
 * Уведомления о возврате — первыми после входа: из `bootstrap.refundNotices` (Р-86), открываются, как только нет
 * системного экрана и обучения; по событию `payment.refunded` — сразу.
 */
export function watchRefunds(app: App): () => void {
  const open = (n: RefundNotice) => {
    void app.call('getWallet').then(setWallet).catch(() => {});
    app.nav.openSheet('MOD-REFUND-NOTICE', { ...n });
  };
  const offWs = onWs((ev) => {
    if (ev.t === 'payment.refunded') {
      const w = wallet.value;
      open({ orderId: ev.d.orderId, xtr: 0, debitedTalons: ev.d.clawback.currencies?.['talons'] ?? 0, talonsDebt: w?.talonsDebt ?? 0 } as RefundNotice);
    }
  });
  let shown = '';
  const offSig = refundNotices.subscribe((list) => {
    const first = list[0];
    if (!first || shown === first.orderId) return;
    const tryOpen = (n = 0) => {
      if (app.nav.state.system || app.nav.state.tutorial) { if (n < 120) setTimeout(() => tryOpen(n + 1), 1000); return; }
      shown = first.orderId;
      open(first);
    };
    tryOpen();
  });
  return () => { offWs(); offSig(); };
}
